import { CustomFieldTypes } from 'idea-toolbox';

import { SessionType } from './session.model';

/**
 * The default definitions of the session proposal form, based on the EGM Wrocław 2027 "Session Proposal Form".
 * They are used when the admins haven't configured the form yet; the admins can then adapt them from the app.
 *
 * Note: name, email, organisation, bio, picture and LinkedIn of the speakers, the title, the method (session type)
 * and the data consent are fixed fields of the proposal and therefore not part of these definitions.
 */

type FieldDef = {
  name: string;
  description?: string;
  type?: CustomFieldTypes;
  obligatory?: boolean;
  enum?: string[];
  enumLabels?: { [key: string]: string };
  min?: number;
  max?: number;
};
type SectionDef = { name: string; description?: string; fields: { [fieldId: string]: FieldDef } };

const YES_NO = ['YES', 'NO'];
const YES_NO_OTHER = ['YES', 'NO', 'OTHER'];
const YES_NO_LABELS = { YES: 'Yes', NO: 'No', OTHER: 'Other' };

/**
 * Map the values of an object, keeping its keys.
 */
const mapValues = <T, R>(obj: { [key: string]: T }, fn: (value: T) => R): { [key: string]: R } =>
  Object.keys(obj).reduce((res, key) => ({ ...res, [key]: fn(obj[key]) }), {});

/**
 * Build a field definition (in the shape expected by `CustomFieldMeta`).
 */
const field = (f: FieldDef): any => ({
  name: { en: f.name },
  description: { en: f.description ?? '' },
  type: f.type ?? CustomFieldTypes.STRING,
  obligatory: !!f.obligatory,
  enum: f.enum,
  enumLabels: f.enumLabels ? mapValues(f.enumLabels, label => ({ en: label })) : undefined,
  min: f.min,
  max: f.max
});
/**
 * Build a block definition (in the shape expected by `CustomBlockMeta`) out of a compact list of sections.
 */
const block = (sections: { [sectionId: string]: SectionDef }): any => ({
  sectionsLegend: Object.keys(sections),
  sections: mapValues(sections, s => ({
    name: { en: s.name },
    description: { en: s.description ?? '' },
    fieldsLegend: Object.keys(s.fields),
    fields: mapValues(s.fields, field)
  }))
});

//
// SHARED QUESTIONS
//

const collaboration: { [fieldId: string]: FieldDef } = {
  collaboration: {
    name: 'Are you proposing this topic in collaboration with another organisation?',
    type: CustomFieldTypes.ENUM,
    enum: YES_NO,
    enumLabels: YES_NO_LABELS,
    obligatory: true
  },
  collaborationOrganisation: { name: 'If yes, please state the name of the organisation.' }
};
const visuals = (what: string): FieldDef => ({
  name: `Are you planning on using a Power Point or other visuals for your ${what}?`,
  type: CustomFieldTypes.ENUM,
  enum: YES_NO_OTHER,
  enumLabels: YES_NO_LABELS,
  obligatory: true
});
const outline: { [fieldId: string]: FieldDef } = {
  outline: {
    name: 'Describe the structure of your session (brief outline)',
    description: 'e.g. "Intro – Activity 1 – Activity 2 – Closing"',
    type: CustomFieldTypes.TEXT,
    obligatory: true
  },
  methods: {
    name: "Activities & methods you'll use",
    description: 'e.g. "discussion, group work, sharing best practices, storytelling, etc."',
    type: CustomFieldTypes.TEXT,
    obligatory: true
  },
  materials: {
    name: 'Materials needed',
    description: 'Write: Item / Amount / Details (e.g.: Markers / 6 / of different colours, for the group activities)',
    type: CustomFieldTypes.TEXT,
    obligatory: true
  }
};
const length = (min: number, max: number, what: string): FieldDef => ({
  name: `Length of the ${what} (minutes)`,
  description: `${what[0].toUpperCase()}${what.slice(1)}s can be ${min} - ${max} minutes long.`,
  type: CustomFieldTypes.NUMBER,
  obligatory: true,
  min,
  max
});

//
// COMMON FORM
//

export const DEFAULT_SESSION_PROPOSAL_FORM_DEF = block({
  session: {
    name: 'Session information',
    fields: {
      topic: {
        name: 'Topic/Theme of the session',
        description:
          'Add major categories and themes that your session touches upon, e.g. higher education, mobility, ' +
          'culture, etc. You can add as many categories as you wish.',
        obligatory: true
      },
      description: {
        name: 'Short description of the session',
        description:
          'Note 1: You may use a maximum of 150 words for this.\n' +
          'Note 2: If your session is approved for the agenda of EGM Wrocław, this text will be shared with ' +
          'participants through the EGM App when they choose and register for the sessions they want to attend.',
        type: CustomFieldTypes.TEXT,
        obligatory: true
      },
      themeStudentRights: {
        name: 'Theme: Bridging the gap between student rights and real-life mobility experiences',
        description: 'How does the topic of your session correspond to the theme of the conference? Check all that apply.',
        type: CustomFieldTypes.BOOLEAN
      },
      themeRecognition: {
        name: 'Theme: Ensuring full and automatic recognition of learning outcomes',
        type: CustomFieldTypes.BOOLEAN
      },
      themeParticipation: {
        name: 'Theme: Strengthening student participation in decision-making processes',
        type: CustomFieldTypes.BOOLEAN
      },
      themeLivingConditions: {
        name: 'Theme: Improving living conditions and support for international students',
        type: CustomFieldTypes.BOOLEAN
      },
      themeOther: { name: 'Theme: other (please specify)' },
      aim: {
        name: 'Define the main aim of your session in one sentence',
        type: CustomFieldTypes.TEXT,
        obligatory: true
      },
      objectives: {
        name: 'Define the objectives of your session (2 to 4 objectives)',
        description: 'Write what participants should get out of the session.',
        type: CustomFieldTypes.TEXT
      }
    }
  },
  final: {
    name: 'Final questions',
    fields: {
      esnCollaboration: {
        name: 'Would you be interested in collaborating with a volunteer from ESN for your session?',
        type: CustomFieldTypes.ENUM,
        enum: YES_NO,
        enumLabels: YES_NO_LABELS,
        obligatory: true
      },
      esnCollaborationDetails: {
        name: 'If yes, please describe how you would envision the collaboration.',
        description: 'If you already have an idea of whom you would like to cooperate with, please also mention this.',
        type: CustomFieldTypes.TEXT
      },
      roomAuditorium: {
        name: 'Room setup: Amphitheatre / Auditorium',
        description: 'Please let us know what kind of room setting you would prefer for your session. Check all that apply.',
        type: CustomFieldTypes.BOOLEAN
      },
      roomRoundtable: { name: 'Room setup: Roundtable', type: CustomFieldTypes.BOOLEAN },
      roomClassroom: {
        name: 'Room setup: Classroom with movable chairs and tables',
        type: CustomFieldTypes.BOOLEAN
      }
    }
  }
});

//
// FORM PER SESSION TYPE (METHOD)
//

export const DEFAULT_SESSION_PROPOSAL_FORM_DEF_PER_TYPE: { [type: string]: any } = {
  [SessionType.TALK]: block({
    talk: {
      name: 'Erasmus Generation Talk',
      description:
        'A TED-style talk about the activities, events, initiatives, projects, experiences and ideas on any topic ' +
        'of importance to the Erasmus Generation. Duration: 20 minutes. Number of speakers: 1. Room format: ' +
        'Theatre. Expected no. of participants: 100.',
      fields: {
        talkType: {
          name: 'Type of talk',
          description: 'What kind of talk are you aiming for?',
          type: CustomFieldTypes.ENUM,
          enum: ['BIG_IDEA', 'SMALL_IDEA', 'DEMO', 'STATEMENT'],
          enumLabels: { BIG_IDEA: 'The big idea', SMALL_IDEA: 'The small idea', DEMO: 'The demo', STATEMENT: 'The statement' },
          obligatory: true
        },
        structure: {
          name: 'Describe the structure of your talk (brief outline)',
          type: CustomFieldTypes.TEXT,
          obligatory: true
        },
        visuals: visuals('presentation'),
        length: {
          name: 'Length of the talk (minutes)',
          description: 'Erasmus Generation Talks can be maximum 20 minutes long.',
          type: CustomFieldTypes.NUMBER,
          obligatory: true,
          min: 1,
          max: 20
        }
      }
    }
  }),
  [SessionType.IGNITE]: block({
    ignite: {
      name: 'Ignite Session',
      description:
        'A fast-paced and dynamic session intended to provoke awareness and stimulate discussion: five minutes ' +
        'accompanied by slides, followed by a Q&A. Duration: 20 minutes (5 min + 15 min Q&A). Number of speakers: 1. ' +
        'Room format: Theatre. Expected no. of participants: 100.',
      fields: {
        structure: {
          name: 'Describe the structure of your 5 min presentation (brief outline)',
          type: CustomFieldTypes.TEXT,
          obligatory: true
        },
        visuals: visuals('presentation')
      }
    }
  }),
  [SessionType.CAMPFIRE]: block({
    campfire: {
      name: 'Campfire Session',
      description:
        'The facilitator sets the mood and tells their story to set the direction of the session, then encourages ' +
        'participants to share their own stories and experiences around the topic. Presentation slides are not ' +
        'permitted. Duration: 60 minutes. Number of facilitators: 1. Room format: Circle. Expected no. of ' +
        'participants: 40.',
      fields: { ...collaboration, ...outline }
    }
  }),
  [SessionType.HUB]: block({
    hub: {
      name: 'Knowledge Hub',
      description:
        'Exchange knowledge, share best practices and utilise the human library of knowledge hidden within the ' +
        'Erasmus Generation. Duration: 60/90 minutes. Number of facilitators: 1-3. Room format: Movable chairs & ' +
        'tables. Expected no. of participants: 40.',
      fields: { ...collaboration, length: length(60, 90, 'session'), ...outline, visuals: visuals('session') }
    }
  }),
  [SessionType.INCUBATOR]: block({
    incubator: {
      name: 'Collaboration Incubator',
      description:
        'Introduce your project idea to the participants and invite them to collaboratively design and plan a ' +
        'project. Duration: 60 to 120 minutes. Number of facilitators: 1-3. Room format: Movable chairs & tables. ' +
        'Expected no. of participants: 40.',
      fields: { ...collaboration, length: length(90, 120, 'session'), ...outline, visuals: visuals('session') }
    }
  }),
  [SessionType.DISCUSSION]: block({
    discussion: {
      name: 'Discussion',
      description:
        'A conversation between multiple people, preferably with different experiences and points of view, with the ' +
        'aim to exchange opinions and ideas. Duration: 60/90 minutes. Number of speakers: 3-4. Number of ' +
        'moderators: 1. Room format: Theatre. Expected no. of participants: 100. Add the speakers and the moderator ' +
        'in the speakers block below.',
      fields: {
        discussionType: {
          name: 'Type of discussion',
          description: 'Please consult the Content Booklet when selecting the discussion type.',
          type: CustomFieldTypes.ENUM,
          enum: ['DEBATE', 'PANEL', 'ROUNDTABLE'],
          enumLabels: { DEBATE: 'Debate', PANEL: 'Panel', ROUNDTABLE: 'Roundtable' },
          obligatory: true
        },
        numberOfSpeakers: {
          name: 'Number of speakers',
          type: CustomFieldTypes.ENUM,
          enum: ['3', '4'],
          obligatory: true
        },
        ...collaboration,
        structure: {
          name: 'Describe the structure of your session',
          description: 'Brief outline of the discussion + questions that you would like to ask panelists.',
          type: CustomFieldTypes.TEXT,
          obligatory: true
        },
        length: length(60, 90, 'discussion'),
        visuals: visuals('discussion')
      }
    }
  }),
  [SessionType.POSTER]: block({
    poster: {
      name: 'Poster Gallery',
      description:
        'Present your ideas, innovations, activities, events, initiatives, projects and best practices in a visual ' +
        'format: an interesting and enticing poster that inspires participants to initiate a discussion. Add the ' +
        'presenter(s) in the speakers block below.',
      fields: {
        posterDescription: {
          name: 'What will your poster be about? (brief description)',
          type: CustomFieldTypes.TEXT,
          obligatory: true
        }
      }
    }
  })
};
